import { ElementRefer } from '@src/slide/application/service/snap-line-service/context/refer/base';
import {
    getElementFrameAfterRotate,
    getFrameAllGrids,
} from '@src/slide/application/service/snap-line-service/util/frame';
import { RefSourceType } from '@src/slide/application/service/snap-line-service/interface/ref.interface';
import type { IRefElement } from '@src/slide/application/service/snap-line-service/interface/ref.interface';
import type {
    IElementBaseModel,
} from '@src/slide/domain/ui-model/element-modeles/Interface/base/element-base-model.interface';
import type { IFrame } from '@src/slide/domain/ui-model/element-modeles/property/frame/frame.interface';
import type { ISnapGridLine } from '@src/slide/application/service/snap-line-service/interface/line.interface';

/**
 * 静态的参照物选取规则
 * 即元素对应的参照物固定，无视元素的位置和其他因素
 * 如边缘参考线和基线参考线的判定，参照物为全体元素和页面本身，不需要动态去选取参照物
 */
export class StaticRefer extends ElementRefer {
    /**
     * 获取当前Slide页面上除了自身的所有元素
     */
    public setRefWithSlideOtherElements() {
        const refElements: IRefElement[] = [];
        const slideElements = this.context.selectionService.currentSlide?.children;

        // 页面内其他元素按规则收集为参照物
        slideElements?.forEach(ele => this.getReferenceRecursive(ele, refElements));
        // 把当前的Slide也作为参照物
        refElements.push(this.getSlideRef());

        this.setRefs(refElements);
    }

    /**
     * 网格化
     * 一个参照元素，将按其矩形的rect，生成在2个方向，按边缘、基线生成6条网格线用于后续判定
     */
    public gridify(
        frame: IFrame,
        ref: IRefElement,
    ): ISnapGridLine[] {
        const {
            topGrid, horizontalBaseLineGrid, bottomGrid, leftGrid, verticalBaseLineGrid, rightGrid,
        } = getFrameAllGrids(frame);

        return [
            topGrid,
            horizontalBaseLineGrid,
            bottomGrid,
            leftGrid,
            verticalBaseLineGrid,
            rightGrid,
        ].map(({ orient, position, type }): ISnapGridLine => ({
            orient,
            position,
            type,
            maxAdsorbGap: this.context.option.maxAdsorbGap,
            source: ref.source,
            refElements: [ref],
        }));
    }

    /**
     * 获取由 slide 作为参照物生成的 ref
     */
    public getSlideRef(frame?: Partial<IFrame>): IRefElement {
        const { slideWidth, slideHeight } = this.context.uiDataService.presentation.props;
        return {
            ele: {
                props: {
                    style: {
                        width: slideWidth,
                        height: slideHeight,
                        ...frame,
                    } as any,
                },
            },
            source: RefSourceType.SLIDE,
            outerFrameOnSlide: { top: 0, left: 0, width: slideWidth, height: slideHeight, ...frame },
        };
    }

    /**
     * 递归的获取所有除自身之外的元素
     *  1. 如果整体选中group，group内的元素都排除在外
     *  2. 如果选中group中的部分元素，只排除选中的元素
     */
    private getReferenceRecursive(ele: IElementBaseModel, refElements: IRefElement[]) {
        if (this.isElementSelected(ele)) {
            return;
        }

        if (this.isGroup(ele)) {
            ele.children?.forEach((ele) => {
                this.getReferenceRecursive(ele, refElements);
            });
        } else {
            refElements.push({
                ele,
                source: RefSourceType.ELEMENT,
                outerFrameOnSlide: getElementFrameAfterRotate(ele),
            });
        }
    }
}
